import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaCalendarAlt, 
  FaUsers, 
  FaArrowRight,
  FaCheck,
  FaMapMarkerAlt,
  FaBed,
  FaWifi,
  FaCar,
  FaUtensils,
  FaTv,
  FaShower,
  FaAirFreshener,
  FaShieldAlt
} from 'react-icons/fa';

const ApartmentBooking = () => {
  const [formData, setFormData] = useState({
    city: '',
    area: '',
    checkInDate: '',
    checkOutDate: '',
    guests: 1,
    apartmentType: '',
    stayDuration: '',
    amenities: [],
    budgetRange: '',
    purpose: '',
    specialRequests: '',
    fullName: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const nigerianCities = [
    { 
      city: 'Lagos',
      areas: ['Victoria Island', 'Lekki', 'Ikeja', 'Yaba', 'Surulere', 'Maryland', 'Magodo', 'Gbagada', 'Festac']
    },
    { 
      city: 'Abuja',
      areas: ['Central Area', 'Garki', 'Wuse', 'Maitama', 'Asokoro', 'Jahi', 'Gwarinpa', 'Kubwa', 'Jabi']
    },
    { 
      city: 'Port Harcourt',
      areas: ['GRA', 'Old GRA', 'Trans Amadi', 'Eliozu', 'Rumuola', 'D-Line', 'Mile 1', 'Ada George']
    },
    { 
      city: 'Kano',
      areas: ['Nassarawa GRA', 'Sabon Gari', 'Fagge', 'Municipal', 'Gwale', 'Tarauni']
    }
  ];

  const apartmentTypes = [
    { value: 'studio', label: 'Studio Apartment', description: '1 room with kitchenette' },
    { value: '1-bedroom', label: '1 Bedroom', description: 'Separate bedroom and living area' },
    { value: '2-bedroom', label: '2 Bedroom', description: 'Two bedrooms, ideal for families' },
    { value: '3-bedroom', label: '3 Bedroom', description: 'Spacious for larger groups' },
    { value: 'duplex', label: 'Duplex', description: 'Two-story apartment' },
    { value: 'penthouse', label: 'Penthouse', description: 'Premium top-floor unit' }
  ];


  const amenityOptions = [
    { value: 'wifi', label: 'WiFi', icon: FaWifi },
    { value: 'parking', label: 'Parking', icon: FaCar },
    { value: 'kitchen', label: 'Full Kitchen', icon: FaUtensils },
    { value: 'ac', label: 'Air Conditioning', icon: FaAirFreshener },
    { value: 'tv', label: 'TV/Cable', icon: FaTv },
    { value: 'washer', label: 'Washer/Dryer', icon: FaShower },
    { value: 'security', label: '24/7 Security', icon: FaShieldAlt },
    { value: 'generator', label: 'Backup Generator', icon: FaShieldAlt }
  ];

  const budgetRanges = [
    { value: 'budget', label: '₦30,000 - ₦80,000', description: 'per night' },
    { value: 'mid-range', label: '₦80,000 - ₦200,000', description: 'per night' },
    { value: 'luxury', label: '₦200,000 - ₦500,000', description: 'per night' },
    { value: 'premium', label: '₦500,000+', description: 'per night' }
  ];

  const purposes = [
    { value: 'business', label: 'Business Trip' },
    { value: 'relocation', label: 'Temporary Relocation' },
    { value: 'vacation', label: 'Vacation/Leisure' },
    { value: 'medical', label: 'Medical Treatment' },
    { value: 'education', label: 'Education/Training' },
    { value: 'family', label: 'Family Visit' },
    { value: 'other', label: 'Other' }
  ];

  const [selectedCity, setSelectedCity] = useState('');
  const [availableAreas, setAvailableAreas] = useState([]);

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    const cityData = nigerianCities.find(c => c.city === city);
    setAvailableAreas(cityData ? cityData.areas : []);
    setFormData({
      ...formData,
      city: city,
      area: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAmenityChange = (amenity) => {
    const updatedAmenities = formData.amenities.includes(amenity)
      ? formData.amenities.filter(a => a !== amenity)
      : [...formData.amenities, amenity];
    
    setFormData({
      ...formData,
      amenities: updatedAmenities
    });
  };

  const handleGuestsChange = (increment) => {
    const newValue = Math.max(1, formData.guests + (increment ? 1 : -1));
    setFormData({
      ...formData,
      guests: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const formDataToSend = {
      ...formData,
      _captcha: false,
      _template: "table",
      _autoresponse: "Thanks for your request! We'll respond shortly.",
      _token: "10d0b5f657f51e47f0b98f7f43832928", // Replace with your actual token
    };
  
    const encoded = new URLSearchParams(formDataToSend).toString();
  
    try {
      await fetch("https://formsubmit.co/10d0b5f657f51e47f0b98f7f43832928", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: encoded,
      });
  
      // Show success message in UI
      setIsSubmitted(true);
  
      // Optionally clear the form
      // setFormData(initialState);
  
      // Hide after a few seconds if needed
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("FormSubmit failed:", error);
      alert("There was a problem submitting the form.");
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 gradient-green overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1716978497661-48bfa34130c0')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FaHome className="text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold">Apartment Booking</h1>
            </div>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Find fully furnished apartments for short-term and extended stays. 
              Perfect for business trips, relocations, or extended vacations across Nigeria.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-2xl text-center max-w-2xl mx-auto"
            >
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <FaCheck className="text-white text-3xl" />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-6">Apartment Request Submitted!</h3>
              <p className="text-lg text-gray-600 mb-4">
                Thank you for choosing Yowa Travels for your apartment booking.
              </p>
              <p className="text-green-600 font-semibold text-lg">
                An agent will be with you shortly to provide the best apartment options for your stay!
              </p>
              <div className="mt-8 p-6 bg-green-50 rounded-2xl">
                <p className="text-sm text-green-700">
                  <strong>What's Next?</strong><br />
                  We'll send you verified apartment listings with photos, floor plans, and availability within an hour.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8">
                {/* Location Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaMapMarkerAlt className="text-green-500" />
                    <span>Location Preference</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">City *</label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleCityChange}
                        className="form-input"
                        required
                      >
                        <option value="">Select city</option>
                        {nigerianCities.map((cityData, index) => (
                          <option key={index} value={cityData.city}>
                            {cityData.city}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="form-label">Preferred Area *</label>
                      <select
                        name="area"
                        value={formData.area}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        disabled={!selectedCity}
                      >
                        <option value="">Select area</option>
                        {availableAreas.map((area, index) => (
                          <option key={index} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Stay Dates */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaCalendarAlt className="text-green-500" />
                    <span>Stay Period</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Check-in Date *</label>
                      <input
                        type="date"
                        name="checkInDate"
                        value={formData.checkInDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Check-out Date *</label>
                      <input
                        type="date"
                        name="checkOutDate"
                        value={formData.checkOutDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={formData.checkInDate || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Guests Count */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaUsers className="text-green-500" />
                    <span>Number of Guests</span>
                  </h4>
                  
                  <div className="border border-gray-200 rounded-xl p-6 max-w-md">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h5 className="font-bold text-gray-800">Total Guests</h5>
                        <p className="text-sm text-gray-600">How many people will be staying?</p>
                      </div>
                      <FaUsers className="text-green-500 text-xl" />
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => handleGuestsChange(false)}
                        disabled={formData.guests <= 1}
                        className="w-10 h-10 rounded-full border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                      >
                        -
                      </button>
                      <span className="text-xl font-bold text-gray-800 mx-4">
                        {formData.guests}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleGuestsChange(true)}
                        className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-green-500 transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Apartment Type */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Apartment Type</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {apartmentTypes.map((type) => (
                      <label
                        key={type.value}
                        className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                          formData.apartmentType === type.value
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="apartmentType"
                          value={type.value}
                          checked={formData.apartmentType === type.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <FaBed className="text-green-500 text-2xl mx-auto mb-3" />
                          <h5 className="font-bold text-gray-800 mb-2">{type.label}</h5>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

    

                {/* Budget Range */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Budget Range</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {budgetRanges.map((budget) => (
                      <label
                        key={budget.value}
                        className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                          formData.budgetRange === budget.value
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="budgetRange"
                          value={budget.value}
                          checked={formData.budgetRange === budget.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <h5 className="font-bold text-gray-800 mb-2">{budget.label}</h5>
                          <p className="text-sm text-gray-600">{budget.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Required Amenities */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Required Amenities</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {amenityOptions.map((amenity) => {
                      const IconComponent = amenity.icon;
                      return (
                        <label
                          key={amenity.value}
                          className={`cursor-pointer border-2 rounded-xl p-4 transition-all duration-300 ${
                            formData.amenities.includes(amenity.value)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.amenities.includes(amenity.value)}
                            onChange={() => handleAmenityChange(amenity.value)}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <IconComponent className="text-green-500 text-2xl mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-800">{amenity.label}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Purpose of Stay */}
                <div className="mb-8">
                  <label className="form-label">Purpose of Stay</label>
                  <select
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select purpose (optional)</option>
                    {purposes.map((purpose) => (
                      <option key={purpose.value} value={purpose.value}>
                        {purpose.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Special Requests */}
                <div className="mb-8">
                  <label className="form-label">Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    className="form-input resize-none"
                    placeholder="Floor preference, pet-friendly, accessibility needs, nearby amenities requirements, or any other special requests..."
                  ></textarea>
                </div>

                {/* Contact Information */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="+234 (0) 900 000 0000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn-yowa-primary text-lg px-12 py-4 inline-flex items-center space-x-3"
                  >
                    <FaHome />
                    <span>Find Apartments</span>
                    <FaArrowRight />
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    No payment required. We'll send you verified apartment listings with availability and pricing.
                  </p>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Choose Apartment Rentals?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enjoy the comfort and convenience of a home away from home with our carefully selected apartments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FaHome,
                title: 'Home Comfort',
                description: 'Fully furnished apartments with all amenities for a comfortable extended stay experience.',
                color: 'bg-green-500'
              },
              {
                icon: FaUtensils,
                title: 'Cost Effective',
                description: 'Save money on dining out with full kitchen facilities and no daily room service charges.',
                color: 'bg-blue-500'
              },
              {
                icon: FaShieldAlt,
                title: 'Verified Properties',
                description: 'All apartments are personally inspected for safety, cleanliness, and quality standards.',
                color: 'bg-purple-500'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-16 h-16 ${benefit.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApartmentBooking;